import React, { useState } from 'react';
import { X, Mail, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { apiUrl } from '../config/api';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ForgotPasswordModal({ isOpen, onClose }: ForgotPasswordModalProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setIsError(false);
    setMessage(null);

    if (!email || !email.includes('@')) {
      setIsError(true);
      setMessage('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(apiUrl('/api/auth/forgot-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: email.trim() })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsError(false);
        setMessage(data.message || 'If an account exists, we sent password reset instructions.');
      } else {
        setIsError(true);
        setMessage(data.message || 'We could not process your request. Please try again later.');
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setIsError(true);
      setMessage('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setMessage(null);
    setIsError(false);
    setLoading(false);
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(1, 2, 6, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        zIndex: 1000
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        style={{
          width: '100%',
          maxWidth: '480px',
          background: '#ffffff',
          borderRadius: '16px',
          boxShadow: '0 24px 48px rgba(1, 2, 6, 0.12)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            border: 'none',
            background: 'transparent',
            color: '#6b7280',
            cursor: 'pointer'
          }}
          aria-label="Close"
          disabled={loading}
        >
          <X size={20} />
        </button>

        <div
          style={{
            padding: '2rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem'
          }}
        >
          <div>
            <h2
              style={{
                fontSize: '1.5rem',
                margin: 0,
                color: 'var(--dark)',
                fontWeight: 600,
                fontFamily: 'var(--montserrat)'
              }}
            >
              Reset your password
            </h2>
            <p
              style={{
                margin: '0.75rem 0 0 0',
                color: 'var(--gray)',
                lineHeight: 1.6,
                fontSize: '0.95rem'
              }}
            >
              Enter the email address associated with your account. We&apos;ll send you a link to choose a new password.
            </p>
          </div>

          {message && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.9rem 1rem',
                borderRadius: '10px',
                border: `1px solid ${isError ? '#fecaca' : '#bbf7d0'}`,
                background: isError ? '#fee2e2' : '#dcfce7',
                color: isError ? '#991b1b' : '#166534',
                fontSize: '0.9rem',
                lineHeight: 1.4
              }}
            >
              {isError ? <AlertCircle size={18} /> : <CheckCircle2 size={18} />}
              <span>{message}</span>
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
          >
            <label style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--gray)' }}>
              Email address
            </label>
            <div style={{ position: 'relative' }}>
              <Mail
                size={18}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '1rem',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af'
                }}
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.85rem 1rem 0.85rem 3rem',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  fontSize: '0.95rem',
                  fontFamily: 'var(--montserrat)'
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: '0.5rem',
                background: loading ? '#d1d5db' : 'var(--orange)',
                color: loading ? '#6b7280' : 'var(--dark)',
                border: 'none',
                borderRadius: '10px',
                padding: '0.85rem 1.5rem',
                fontWeight: 600,
                fontFamily: 'var(--montserrat)',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s ease'
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="spinning" size={18} />
                  Sending...
                </>
              ) : (
                'Send reset link'
              )}
            </button>
          </form>

          <button
            onClick={handleClose}
            disabled={loading}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--gray)',
              fontSize: '0.85rem',
              cursor: 'pointer',
              alignSelf: 'center',
              textDecoration: 'underline'
            }}
          >
            Back to sign in
          </button>
        </div>
      </div>
    </div>
  );
}

